export type ReportDateRangeError = 'required' | 'order' | null;
export type ReportDateRangeCorrection = 'future' | 'maxRange';

interface ReportDateRangeBase {
  corrections: ReportDateRangeCorrection[];
}

interface InvalidReportDateRange extends ReportDateRangeBase {
  error: Exclude<ReportDateRangeError, null>;
  dateFrom: Date | null;
  dateTo: Date | null;
}

interface ValidReportDateRange extends ReportDateRangeBase {
  error: null;
  dateFrom: Date;
  dateTo: Date;
}

export type ReportDateRange = InvalidReportDateRange | ValidReportDateRange;

const toCalendarDate = (value?: Date | string | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const addCalendarYear = (date: Date): Date => {
  const result = new Date(date.getFullYear() + 1, date.getMonth(), date.getDate());
  if (result.getMonth() !== date.getMonth()) {
    return new Date(date.getFullYear() + 1, date.getMonth() + 1, 0);
  }
  return result;
};

export const toReportDateTime = (date: Date): string => new Date(Date.UTC(
  date.getFullYear(),
  date.getMonth(),
  date.getDate()
)).toISOString();

export const normalizeReportDateRange = (
  dateFrom?: Date | string | null,
  dateTo?: Date | string | null,
  currentDate: Date = new Date()
): ReportDateRange => {
  let normalizedFrom = toCalendarDate(dateFrom);
  let normalizedTo = toCalendarDate(dateTo);
  const today = toCalendarDate(currentDate) as Date;
  const corrections: ReportDateRangeCorrection[] = [];

  if (!normalizedFrom || !normalizedTo) {
    return { error: 'required', corrections, dateFrom: normalizedFrom, dateTo: normalizedTo };
  }

  if (normalizedFrom > today) {
    normalizedFrom = new Date(today.getTime());
    corrections.push('future');
  }
  if (normalizedTo > today) {
    normalizedTo = new Date(today.getTime());
    if (!corrections.includes('future')) {
      corrections.push('future');
    }
  }

  if (normalizedTo < normalizedFrom) {
    return { error: 'order', corrections, dateFrom: normalizedFrom, dateTo: normalizedTo };
  }

  const maximumDateTo = addCalendarYear(normalizedFrom);
  if (normalizedTo > maximumDateTo) {
    normalizedTo = maximumDateTo;
    corrections.push('maxRange');
  }

  return { error: null, corrections, dateFrom: normalizedFrom, dateTo: normalizedTo };
};
